<template>
  <div class="space-y-4">
    <DividerHeader :title="$t('rollout.tasks.email-confirmation.title')" />

    <Alert type="information">
      {{ $t('rollout.tasks.email-confirmation.info-text') }}
    </Alert>

    <Checkbox
      v-model="sendConfirmationMail"
      id="has-client-task-email-confirmation"
      :label="$t('rollout.tasks.email-confirmation.send-confirmation-mail')"
    />

    <TagsInput
      v-if="sendConfirmationMail"
      v-model="innerRecipients"
      id="client-task-email"
      :placeholder="$t('rollout.tasks.email-confirmation.emails-placeholder')"
      :label="$t('rollout.tasks.email-confirmation.recipient-emails')"
      string
      required
    />
  </div>
</template>

<script lang="ts" setup>
import type { ClientBoxClient } from '../../Box.vue';
import { useApiCustomFieldValue } from '@/composables/api-services/api-custom-field-value';

const props = defineProps({
  client: {
    type: Object as PropType<ClientBoxClient>,
  },
  recipients: {
    type: Array as PropType<string[]>,
  },
  emailCustomField: {
    type: String,
  },
});

const emit = defineEmits(['update:recipients']);

const sendConfirmationMail = ref(false);

const innerRecipients = computed({
  get: () => props.recipients ?? [],
  set: (newValue) => emit('update:recipients', newValue),
});

const apiCustomFieldValue = useApiCustomFieldValue();

const fetchEmailCustomFieldValue = async () => {
  if (!props.emailCustomField || !props.client?._id) {
    return;
  }

  try {
    const customFieldValuesByClientResponse =
      await apiCustomFieldValue.getByClientId({
        clientId: props.client._id,
      });

    const emailCustomFieldValue = customFieldValuesByClientResponse.item?.values
      ? customFieldValuesByClientResponse.item.values[props.emailCustomField]
      : undefined;

    return emailCustomFieldValue;
  } catch (error) {
    // Show Error in sentry, but no need to show user any messages here,
    // if fetching emailCustomFieldValue failed
    useErrorHandler(error, false);
    return null;
  }
};

const initEmailCustomFieldValue = async () => {
  const emailCustomFieldValue = await fetchEmailCustomFieldValue();

  if (!innerRecipients.value.length && !!emailCustomFieldValue) {
    if (Array.isArray(emailCustomFieldValue)) {
      innerRecipients.value = emailCustomFieldValue;
    } else {
      innerRecipients.value = [emailCustomFieldValue];
    }
  }
};

const clearEmailCustomField = () => {
  innerRecipients.value = [];
};

watch(
  [computed(() => props.emailCustomField), computed(() => props.client)],
  ([emailCustomField, client]) => {
    if (!props.client?._id) {
      clearEmailCustomField();
      return;
    }
    if (props.client?.clientUser?.email) {
      innerRecipients.value = [props.client.clientUser?.email];
    } else if (!!emailCustomField && !!client?._id) {
      initEmailCustomFieldValue();
    } else {
      clearEmailCustomField();
    }
  },
  { immediate: true }
);
</script>
